<template>
  <div class="card">
    <header class="card-header">
      <span class="card-header-title">
        {{ title }}
      </span>
    </header>
    <div v-if="!isLoading" class="card-content">
      <div class="columns is-mobile is-vcentered">
        <div class="column is-3 has-text-grey-light has-text-weight-bold">
          Hit Rate
        </div>
        <div class="column">
          <h3
            :class="[
              'title',
              'is-3',
              hitRateColorClass,
            ]"
          >
            {{ convertFractionToPercent(hitGoalPercentage) }}
            <span class="is-size-5">%</span>
          </h3>
        </div>
      </div>
      <div
        v-for="metric in Object.entries(goalCountsMinusUnmarked)"
        :key="metric[0]"
        class="columns is-mobile is-vcentered"
      >
        <div class="column is-3">
          <p class="has-text-weight-bold has-text-grey-light">{{ getGoalLabel(metric[0]) }}</p>
        </div>
        <div class="column">
          <h4 class="title is-4">
            {{ metric[1] }}
          </h4>
        </div>
      </div>
      <div class="columns is-mobile is-vcentered">
        <div class="column is-3">
          <p class="has-text-weight-bold has-text-grey-light">Total</p>
        </div>
        <div class="column">
          <h4 class="title is-4">
            {{ totalGoalCount }}
          </h4>
        </div>
      </div>
    </div>
    <div v-else style="position: relative;" class="u-p-b-xlarge">
      <b-loading v-if="isLoading" :is-full-page="false" :active.sync="isLoading"></b-loading>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { goalTypes } from '@/constants/Types';
import { convertFractionToPercent } from '@/helpers/numberHelpers';
import { isUserOOO } from '@/helpers/dateHelpers';

export default {
  name: 'DailyGoal',

  props: {
    isLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    activeUserId: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      isEditingGoal: false,
      isGoalDetailModalOpen: false,
    };
  },

  computed: {
    ...mapState({
      users: state => state.user.users,
      goals: state => state.goal.goals,
    }),
    ...mapGetters([
      'activeWorkspace',
    ]),
    activeUser() {
      return this.users[this.activeUserId];
    },
    nonOOOGoals() {
      return Object.values(this.goals)
        .filter(goal => goal.userId === this.activeUserId && !isUserOOO(this.activeUser, goal.goalDate));
    },
    goalCountsMinusUnmarked() {
      const counts = {};
      this.nonOOOGoals.forEach((goal) => {
        if (goal.goalStatus !== 0) {
          counts[String(goal.goalStatus)] = (counts[String(goal.goalStatus)] || 0) + 1;
        }
      });
      return counts;
    },
    totalGoalCount() {
      return Object.values(this.goalCountsMinusUnmarked).reduce((a, b) => (a + b), 0);
    },
    hitGoalPercentage() {
      return this.totalGoalCount > 0 ? (this.goalCountsMinusUnmarked['1'] || 0) / this.totalGoalCount : 0;
    },
    hitRateColorClass() {
      let className = '';
      if (this.hitGoalPercentage > 0.9) {
        className = 'has-text-success';
      } else if (this.hitGoalPercentage > 0.8) {
        className = 'has-text-info';
      } else if (this.hitGoalPercentage > 0.7) {
        className = 'has-text-warning';
      } else if (this.totalGoalCount === 0) {
        className = 'has-text-grey-light';
      } else {
        className = 'has-text-danger';
      }

      return className;
    },
  },

  methods: {
    convertFractionToPercent,
    getGoalLabel(goalStatus) {
      return Object.values(goalTypes).filter(goalType => goalType.value === parseInt(goalStatus, 10))[0].label;
    },
  },
};
</script>

<style lang="scss" scoped>
.columns {
  &:not(:last-child) {
    border-bottom: 1px solid $grey-lighter;
  }
}
</style>
